import { useState } from "react";
import API from "../../lib/api";
import { Section } from "./PortalPage";
import { fmtMoney } from "./portalUtils";
import { FilePdf, TreeStructure } from "@phosphor-icons/react";
import { toast } from "sonner";

const pct = (v) => (v == null ? "—" : `${v}%`);
const num = (v, unit = "") => (v == null ? "—" : `${v}${unit}`);

/* Main-client portal: spend & SLA per sub-client + monthly statement download. */
export function ByClientReport({ rows }) {
  if (!rows || rows.length === 0) return null;
  const totals = rows.reduce((t, r) => ({ requests: t.requests + r.requests, jobs: t.jobs + r.jobs, completed: t.completed + r.completed_jobs, emergencies: t.emergencies + r.emergencies, invoiced: t.invoiced + r.invoiced, paid: t.paid + r.paid, outstanding: t.outstanding + r.outstanding }), { requests: 0, jobs: 0, completed: 0, emergencies: 0, invoiced: 0, paid: 0, outstanding: 0 });
  return (
    <Section title="By client — spend & SLA" testid="report-by-client">
      <div className="overflow-x-auto -mx-4 px-4">
        <table className="w-full text-xs" data-testid="by-client-report">
          <thead className="text-[10px] uppercase tracking-wider text-muted-foreground">
            <tr className="border-b border-border">
              <th className="text-left py-2 pr-3">Client</th><th className="text-right py-2 px-2">Requests</th><th className="text-right py-2 px-2">Jobs done</th><th className="text-right py-2 px-2">Emergencies</th>
              <th className="text-right py-2 px-2">Avg response</th><th className="text-right py-2 px-2">SLA on time</th><th className="text-right py-2 px-2">Request → done</th>
              <th className="text-right py-2 px-2">Invoiced</th><th className="text-right py-2 px-2">Paid</th><th className="text-right py-2 pl-2">Outstanding</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.client_id} className="border-b border-border/60" data-testid={`by-client-row-${r.client_id}`}>
                <td className="py-2 pr-3 font-medium">{r.name}{r.is_self && <span className="ml-1 text-[9px] uppercase text-muted-foreground">(ourselves)</span>}</td>
                <td className="text-right py-2 px-2 font-mono">{r.requests}</td>
                <td className="text-right py-2 px-2 font-mono">{r.completed_jobs} / {r.jobs}</td>
                <td className="text-right py-2 px-2 font-mono">{r.emergencies}</td>
                <td className="text-right py-2 px-2 font-mono">{num(r.avg_emergency_response_hours, "h")}</td>
                <td className={`text-right py-2 px-2 font-mono ${r.emergency_sla_on_time_pct != null && r.emergency_sla_on_time_pct < 90 ? "text-amber-600" : ""}`}>{pct(r.emergency_sla_on_time_pct)}</td>
                <td className="text-right py-2 px-2 font-mono">{num(r.avg_request_to_completion_days, "d")}</td>
                <td className="text-right py-2 px-2 font-mono">{fmtMoney(r.invoiced)}</td>
                <td className="text-right py-2 px-2 font-mono text-emerald-600">{fmtMoney(r.paid)}</td>
                <td className={`text-right py-2 pl-2 font-mono ${r.outstanding > 0 ? "text-amber-600 font-semibold" : ""}`}>{fmtMoney(r.outstanding)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot className="font-semibold">
            <tr data-testid="report-by-client-totals">
              <td className="py-2 pr-3">All clients</td><td className="text-right py-2 px-2 font-mono">{totals.requests}</td><td className="text-right py-2 px-2 font-mono">{totals.completed} / {totals.jobs}</td><td className="text-right py-2 px-2 font-mono">{totals.emergencies}</td>
              <td colSpan={3}></td><td className="text-right py-2 px-2 font-mono">{fmtMoney(totals.invoiced)}</td><td className="text-right py-2 px-2 font-mono text-emerald-600">{fmtMoney(totals.paid)}</td><td className="text-right py-2 pl-2 font-mono">{fmtMoney(totals.outstanding)}</td>
            </tr>
          </tfoot>
        </table>
      </div>
      <p className="text-[10px] text-muted-foreground mt-2 inline-flex items-center gap-1"><TreeStructure size={11} /> Invoices for sub-client work are billed to you and attributed here to the client the work was for. SLA = emergencies acknowledged within their urgency window.</p>
    </Section>
  );
}

export function StatementDownload({ testid = "statement-download" }) {
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [busy, setBusy] = useState(false);
  const open = async () => {
    setBusy(true);
    try {
      const { data } = await API.get("/client-hub/statement.pdf", { params: { month }, responseType: "blob" });
      window.open(URL.createObjectURL(new Blob([data], { type: "application/pdf" })), "_blank");
    } catch (e) { toast.error("Couldn't build the statement"); }
    finally { setBusy(false); }
  };
  return (
    <div className="flex items-center gap-1.5" data-testid={testid}>
      <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="h-8 rounded-md border border-input bg-background px-2 text-xs" data-testid={`${testid}-month`} />
      <button type="button" onClick={open} disabled={busy} className="inline-flex items-center h-8 px-2.5 text-xs rounded-md border border-border bg-card hover:bg-muted/40 disabled:opacity-50" data-testid={`${testid}-btn`}><FilePdf size={13} className="mr-1" /> {busy ? "Building…" : "Statement PDF"}</button>
    </div>
  );
}
